import { useEffect, useRef, useState, type RefObject } from 'react';
import { clamp01 } from './tokens';

/**
 * One passive scroll/resize listener and one rAF per frame for the whole page.
 * Sections subscribe with their element; they receive geometry-derived progress,
 * never raw scroll events. Native scrolling is never intercepted.
 */
export interface SectionMetrics {
  /** 0 when the section top meets the viewport bottom, 1 when its bottom leaves the top. */
  pass: number;
  /** Sticky-track progress: 0 when the top reaches the viewport top, 1 when the bottom reaches the viewport bottom. */
  track: number;
  /** True while any part of the section is within the viewport. */
  visible: boolean;
  /** True while the section spans the viewport's horizontal centre line. */
  centred: boolean;
}
type Listener = (metrics: SectionMetrics) => void;

const subscribers = new Map<Element, Set<Listener>>();
let frame = 0;
let attached = false;

function measure(element: Element, viewport: number): SectionMetrics {
  const rect = element.getBoundingClientRect();
  const scrollable = rect.height - viewport;
  return {
    pass: clamp01((viewport - rect.top) / (rect.height + viewport)),
    track: scrollable > 0 ? clamp01(-rect.top / scrollable) : rect.top <= 0 ? 1 : 0,
    visible: rect.bottom > 0 && rect.top < viewport,
    centred: rect.top <= viewport * 0.5 && rect.bottom > viewport * 0.5,
  };
}
function flush() {
  frame = 0;
  const viewport = window.innerHeight;
  for (const [element, listeners] of subscribers) {
    const metrics = measure(element, viewport);
    for (const listener of listeners) listener(metrics);
  }
}
function schedule() { if (!frame) frame = requestAnimationFrame(flush); }
function attach() {
  if (attached) return; attached = true;
  window.addEventListener('scroll', schedule, { passive: true });
  window.addEventListener('resize', schedule, { passive: true });
}
function detachIfIdle() {
  if (subscribers.size || !attached) return;
  attached = false; window.removeEventListener('scroll', schedule); window.removeEventListener('resize', schedule);
  if (frame) cancelAnimationFrame(frame); frame = 0;
}

export function observeSection(element: Element, listener: Listener): () => void {
  attach();
  const set = subscribers.get(element) ?? new Set<Listener>(); set.add(listener); subscribers.set(element, set);
  listener(measure(element, window.innerHeight));
  return () => { set.delete(listener); if (!set.size) subscribers.delete(element); detachIfIdle(); };
}
/** Forces a measurement on the next frame (e.g. after fonts/layout settle). */
export const requestMeasure = schedule;

/** Imperative subscription for per-frame consumers (3D, globe, SVG) — no React re-render. */
export function useSectionMetrics(ref: RefObject<Element | null>, listener: Listener) {
  const latest = useRef(listener); latest.current = listener;
  useEffect(() => {
    const element = ref.current; if (!element) return;
    return observeSection(element, metrics => latest.current(metrics));
  }, [ref]);
}

/** Quantised React state for light consumers (e.g. which step is active). */
export function useTrackStep(ref: RefObject<Element | null>, steps: number) {
  const [step, setStep] = useState(0);
  useSectionMetrics(ref, ({ track }) => {
    const next = Math.min(steps - 1, Math.floor(track * steps));
    setStep(previous => (previous === next ? previous : next));
  });
  return step;
}
